import Debug from 'debug';
import { ExamQuestion,  ExamCurrentQuestion } from './exam-question'
import { QuizQuestionsState, QuizNavigationState, NavigationState, InputNavigation,
         QuizTimeState, QuizStateMachinePhaseState, NavigationOptions,
         QuizPlayerInputQuestionStates,
         QuizQuestionTimeState,
         InputQuestionTimes} from '@/app/quiz-player/api';
import { ExamDetails, defaultExamDetails, ExamStateData, ExamResult } from './exam-state-data'
import { ExamStateMachinePhase } from '../states';

const debug = Debug('exam-state-machine-data')

interface CommonParameters {
  sessionId: string
  startTime: Date
  questions: ExamQuestion[]
  examDetails: ExamDetails
}

interface InputState {
  questionStates: QuizPlayerInputQuestionStates
  navigation: InputNavigation
  questionTimes: InputQuestionTimes
}

export class ExamStateMachineData implements NavigationState {

  private sessionId: string
  private navigation: QuizNavigationState
  private questionsState: QuizQuestionsState
  private phaseState: QuizStateMachinePhaseState
  private timeState: QuizTimeState
  private examState: ExamStateData
  private questionTimes: QuizQuestionTimeState
  private currentError: unknown

  constructor() {
    this.initialize()
  }

  initialize() {
    this.sessionId = ''
    this.navigation = new QuizNavigationState(0)
    this.questionsState = new QuizQuestionsState([], { questionsDisabled: false })
    this.phaseState = new QuizStateMachinePhaseState()
    this.timeState = new QuizTimeState()
    this.examState = new ExamStateData(defaultExamDetails())
    this.questionTimes = new QuizQuestionTimeState(0)
  }

  started(options: CommonParameters) {

    debug('started', options)
    this.createState(options)
    this.updateCurrentQuestion(0, { updateLastIndex: true })
  }

  continued(options: CommonParameters, { questionStates, navigation, questionTimes }: InputState) {
    debug('continued', options, questionStates, navigation, questionTimes)
    this.createState(options)

    this.questionsState.initializeQuestionStates(questionStates)
    this.navigation.load(navigation)
    this.questionTimes.load(questionTimes)
  }

  private createState({ sessionId, startTime, questions, examDetails}: CommonParameters) {
    this.sessionId = sessionId
    this.questionsState = new QuizQuestionsState(questions, { questionsDisabled: false })
    this.navigation = new QuizNavigationState(questions.length)
    this.examState = new ExamStateData(examDetails)
    this.timeState = new QuizTimeState()
    this.timeState.start(startTime)
    this.questionTimes = new QuizQuestionTimeState(questions.length)
  }

  updatePhase(phase: ExamStateMachinePhase) {
    this.phaseState.updatePhase(phase)
  }

  setCurrentError(error: unknown) {
    this.currentError = error
  }

  finished(result: ExamResult) {
    this.examState.updateResult(result)
  }

  review(questionIndex: number, options: NavigationOptions) {
    this.updateCurrentQuestion(questionIndex, options)
  }

  goToNextQuestion(options: NavigationOptions) {
    return this.navigation.goToNextQuestion(options)
  }

  goToPreviousQuestion(options: NavigationOptions) {
    return this.navigation.goToPreviousQuestion(options)
  }

  updateCurrentQuestion(questionIndex: number, options: NavigationOptions) {
    debug('updateCurrentQuestion', questionIndex)
    return this.navigation.updateCurrentQuestion(questionIndex, options)
  }

  get isInProgress() {
    return this.phaseState.isInProgress
  }

  get isInSummary() {
    return this.phaseState.isInSummary
  }

  get isInReview() {
    return this.phaseState.isInReview
  }

  get questionTexts() {
    return this.questionsState.questionTexts
  }

  get questionPhases() {
    return this.questionsState.questionPhases
  }

  get examDetails() {
    return this.examState.examDetails
  }

  get hasTimeLimit() {
    return this.examState.hasTimeLimit
  }

  durationSeconds(now: Date) {
    return this.timeState.currentDurationSeconds(now)
  }

  refreshRemainingTimeSeconds() {
    const now = new Date()
    const durationSeconds = this.timeState.currentDurationSeconds(now)
    let remainingSeconds = this.examDetails.timeLimitSeconds - durationSeconds
    if (remainingSeconds < 0) {
      remainingSeconds = 0
    }

    this.examState.updateRemainingTimeSeconds(remainingSeconds)
    return remainingSeconds
  }

  get remainingTimeSeconds() {
    return this.examState.remainingTimeSeconds
  }

  registerQuestionShownTime(time: Date) {
    const index = this.checkCurrentQuestion()
    this.questionTimes.shown(index, time)
  }

  registerQuestionAnsweredTime(time: Date) {
    const index = this.checkCurrentQuestion()
    this.questionTimes.answered(index, time)
  }

  finalizeQuestionTimes() {
    this.questionTimes.finalize()
  }

  get currentQuestionTimes() {
    return this.questionTimes.times
  }

  private checkCurrentQuestion() {
    if (!this.navigation.isAtQuestion) {
      throw new Error('Coding error: invalid current index: ' + this.navigation.questionIndex)
    }

    return this.navigation.questionIndex
  }

  questionForStateMachine(questionIndex: number) {
    return this.questionsState.questionForStateMachine(questionIndex)
  }

  get currentQuestion(): ExamCurrentQuestion | null {

    if (!this.navigation.isAtQuestion) {
      return null
    }

    const question = this.questionsState.question(this.navigation.questionIndex)

    const result = {
      question: question.question,
      state: question.state,
      answer: question.answer,
      evaluationResult: {
        score: question.evaluation.details.score,
        result: question.evaluation.result
      },
      viewState: this.questionEvaluationState
    }

    return result
  }

  private get questionEvaluationState() {
    const inReview = this.isInReview
    return {
      showEvaluate: false,
      canEvaluate: false,
      showScore: inReview,
      enabled: !inReview,
      evaluated: inReview,
      showSolution: inReview
    }
  }

  get questionScores() {
    return this.questionsState.questionScores
  }

  get examResult() {
    return this.examState.examResult
  }

  get questionCount() {
    return this.questionsState.questionCount
  }

  get notAnsweredQuestionCount() {
    return this.questionsState.notAnsweredQuestionCount({ markNotEvaluatedAsNonAnswered: false })
  }

  get questionIndex() {
    return this.navigation.questionIndex
  }

  get lastVisitedIndex() {
    return this.navigation.lastVisitedIndex
  }

  get isFirstQuestion() {
    return this.navigation.isFirstQuestion
  }

  get isLastQuestion() {
    return this.navigation.isLastQuestion
  }

  get currentSessionId() {
    return this.sessionId
  }

  exportQuestionStateAt(index: number) {
    return this.questionsState.exportState(index)
  }

  exportNavigationState() {
    return this.navigation.exportState()
  }

  exportQuestionTimesState() {
    return this.questionTimes.exportTimes()
  }

  get error() {
    return this.currentError
  }
}
